/**
 * Extracts a human-readable message from a Data Builder API error. The backend
 * returns errors as a JSON body whose `detail` is either a single string or a
 * FastAPI-style array of `{ msg }` entries; the latter are combined into one
 * message.
 */
export const extractDataBuilderErrorMessage = (
  error: unknown,
  fallback: string = 'An error occurred.'
): string => {
  const isValidationErrorDetail = (
    detail: unknown
  ): detail is { msg: string }[] =>
    Array.isArray(detail) &&
    detail.length > 0 &&
    detail.every(
      (item) =>
        item !== null &&
        typeof item === 'object' &&
        'msg' in item &&
        typeof (item as { msg: unknown }).msg === 'string'
    );

  const parseErrorDetail = (raw: string): unknown => {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (
        parsed === null ||
        typeof parsed !== 'object' ||
        !('detail' in parsed)
      ) {
        return null;
      }
      return (parsed as { detail: unknown }).detail;
    } catch {
      return null;
    }
  };

  if (!(error instanceof Error) || error.message.length === 0) {
    return fallback;
  }

  const detail = parseErrorDetail(error.message);

  if (typeof detail === 'string' && detail.length > 0) {
    return detail;
  }

  if (isValidationErrorDetail(detail)) {
    return detail.map((entry) => entry.msg).join(' ');
  }

  return error.message;
};
